// YERİ: src/app/domains/site/sections/contact-form.section.ts
// İletişim — form (lead) + iletişim bilgileri + opsiyonel harita.
// clientId verilirse form gönderimi public lead endpoint'ine gider (CrmEnabled
// ise CrmContact oluşur); verilmezse yalnız görsel + yerel teşekkür.

import { Component, computed, inject, input, signal } from '@angular/core';
import { MIcon } from '@/app/shared/ui/m-icon';
import { TenantWebService } from '../core/tenant-web.service';
import { SiteConfig, cfg } from './section.model';
import { SiteChromeText } from '../core/site-chrome-text';

@Component({
  selector: 'sec-contact-form',
  standalone: true,
  imports: [MIcon],
  template: `
    <section id="contact" class="bg-white py-24 dark:bg-neutral-950">
      <div class="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2 lg:px-8">
        <!-- Bilgi -->
        <div>
          <p class="text-sm font-semibold uppercase tracking-widest" style="color:var(--site-accent)">{{ eyebrow() }}</p>
          <h2 class="mt-3 font-[family-name:var(--site-head-font)] text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">{{ title() }}</h2>
          @if (subtitle()) { <p class="mt-4 text-lg text-neutral-600 dark:text-neutral-400">{{ subtitle() }}</p> }
          <dl class="mt-8 space-y-5">
            @if (address()) {
              <div class="flex items-start gap-4">
                <span class="flex size-10 shrink-0 items-center justify-center rounded-lg text-white" style="background-color:var(--site-primary)"><m-icon name="map-pin" size="sm" /></span>
                <div><dt class="text-sm font-medium text-neutral-500">{{ metin.t('address') }}</dt>
                  <dd class="text-neutral-900 dark:text-white">
                    @if (mapEnabled()) { <a [href]="mapUrl()" target="_blank" rel="noopener" class="hover:underline">{{ address() }}</a> } @else { {{ address() }} }
                  </dd>
                </div>
              </div>
            }
            @if (phone()) {
              <div class="flex items-start gap-4">
                <span class="flex size-10 shrink-0 items-center justify-center rounded-lg text-white" style="background-color:var(--site-primary)"><m-icon name="phone" size="sm" /></span>
                <div><dt class="text-sm font-medium text-neutral-500">{{ metin.t('phone') }}</dt>
                  <dd class="text-neutral-900 dark:text-white">
                    @if (callEnabled()) { <a [href]="'tel:' + telHref()" class="hover:underline">{{ phone() }}</a> } @else { {{ phone() }} }
                  </dd>
                </div>
              </div>
            }
            @if (email()) {
              <div class="flex items-start gap-4">
                <span class="flex size-10 shrink-0 items-center justify-center rounded-lg text-white" style="background-color:var(--site-primary)"><m-icon name="mail" size="sm" /></span>
                <div><dt class="text-sm font-medium text-neutral-500">{{ metin.t('cf.email') }}</dt>
                  <dd class="text-neutral-900 dark:text-white">
                    @if (emailEnabled()) { <a [href]="'mailto:' + email()" class="hover:underline">{{ email() }}</a> } @else { {{ email() }} }
                  </dd>
                </div>
              </div>
            }
          </dl>
        </div>

        <!-- Form -->
        <div class="rounded-2xl border border-neutral-200 bg-neutral-50 p-8 dark:border-neutral-800 dark:bg-neutral-900">
          @if (sent()) {
            <div class="flex h-full flex-col items-center justify-center py-10 text-center">
              <span class="flex size-14 items-center justify-center rounded-full text-white" style="background-color:var(--site-primary)"><m-icon name="check" /></span>
              <p class="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">{{ metin.t('cf.received') }}</p>
              <p class="mt-1 text-sm text-neutral-500">{{ metin.t('cf.receivedHint') }}</p>
            </div>
          } @else {
            <div class="space-y-4">
              <input class="w-full rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
                     [placeholder]="metin.t('cf.namePh')" [value]="name()" (input)="name.set($any($event.target).value)" />
              <input class="w-full rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
                     type="email" [placeholder]="metin.t('cf.email')" [value]="emailVal()" (input)="emailVal.set($any($event.target).value)" />
              <input class="w-full rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
                     [placeholder]="metin.t('phone')" [value]="phoneVal()" (input)="phoneVal.set($any($event.target).value)" />
              <textarea rows="4" class="w-full rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
                        [placeholder]="metin.t('cf.messagePh')" [value]="message()" (input)="message.set($any($event.target).value)"></textarea>
              <button type="button" [disabled]="!canSend() || sending()"
                      class="w-full rounded-lg px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
                      style="background-color:var(--site-primary)" (click)="submit()">
                {{ sending() ? metin.t('sending') : metin.t('cf.send') }}
              </button>
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class ContactFormSection {
  /** Ziyaretçiye görünen kabuk metinleri — dil sitenin ayarından. */
  protected metin = inject(SiteChromeText);
  private svc = inject(TenantWebService);
  config = input<SiteConfig>({});
  clientId = input<number>(0);

  protected eyebrow = computed(() => cfg(this.config(), 'eyebrow', 'İletişim'));
  protected title = computed(() => cfg(this.config(), 'title', 'Bize Ulaşın'));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', 'Sorularınız için formu doldurun, en kısa sürede dönüş yapalım.'));
  protected address = computed(() => cfg(this.config(), 'address', 'Merkez Mah. Adalet Cad. No:1, İstanbul'));
  protected phone = computed(() => cfg(this.config(), 'phone', '+90 212 000 00 00'));
  protected email = computed(() => cfg(this.config(), 'email', 'info@hukukburosu.com'));

  // Tıklanabilirlik (müşteri açıp kapatır). 'false' → düz metin.
  private on = (k: string, def = 'true') => String(cfg(this.config(), k, def)).toLowerCase() !== 'false';
  protected mapEnabled = computed(() => this.on('addressMap'));
  protected callEnabled = computed(() => this.on('phoneCall'));
  protected emailEnabled = computed(() => this.on('emailLink'));
  protected mapUrl = computed(() => 'https://www.google.com/maps?q=' + encodeURIComponent(this.address()));
  protected telHref = computed(() => this.phone().replace(/[^+0-9]/g, ''));

  protected name = signal('');
  protected emailVal = signal('');
  protected phoneVal = signal('');
  protected message = signal('');
  protected sending = signal(false);
  protected sent = signal(false);
  protected canSend = computed(() => this.name().trim().length > 0 && (this.emailVal().trim().length > 0 || this.phoneVal().trim().length > 0));

  protected submit() {
    if (!this.canSend()) return;
    const id = this.clientId();
    if (!id) { this.sent.set(true); return; } // önizleme / clientId yok → yerel teşekkür
    this.sending.set(true);
    const payload = { name: this.name(), email: this.emailVal(), phone: this.phoneVal(), message: this.message() };
    // "Formlar" gelen kutusuna yaz (asıl mesaj burada görünür).
    this.svc.postForm(id, { ...payload, subject: this.metin.t('cf.formLabel') }).subscribe({
      next: () => { this.sending.set(false); this.sent.set(true); },
      error: () => { this.sending.set(false); this.sent.set(true); },
    });
    // Ayrıca CRM'e lead olarak ekle (CrmEnabled ise CrmContact oluşur).
    this.svc.postLead(id, payload).subscribe({ next: () => {}, error: () => {} });
  }
}
